import React, { useEffect, useState, useMemo, Suspense } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/utils/api";
import { useTranslation } from "react-i18next";
import Pagination from "@mui/material/Pagination";
import { useSelector } from "react-redux";
import { WorkerProfileCard } from "@/components/workerProfileCard";
import { Spinner } from "@/shared/spinner/spinner";
import { Helmet } from "react-helmet-async";
import {
  getServiceId,
  normalizeWorkersListResponse,
  normalizeServiceCategoriesResponse,
} from "@/vike/ssrHelpers";
import { findServiceBySlugInCategories } from "@/utils/slugify";
import { trackMetaViewContent } from "@/utils/metaPixel";
import Popup from "@/shared/popups/popupDetails";
import { InlineLazyFallback } from "@/shared/RouteLoadingFallback";
import type { WorkerProfile } from "@/types/userTypes";
import { isAuthenticated } from "@/shared/services/credentialsService";
import { QuoteSelectionModeToggle } from "@/components/quote/QuoteSelectionModeToggle";
import {
  getProviderUserId,
  getWorkerProfileId,
} from "@/utils/workerProfileUtils";
// Lazy import RequestQuote (heavy form)
const RequestQuote = React.lazy(() => import("@/pages/hire/request-quote"));


type ServiceDetailPageProps = {
  initialService?: any;
  /** First page of providers from SSR; undefined when not from Vike (SPA route). */
  initialWorkers?: any;
};

const ServiceDetailPage = ({
  initialService = null,
  initialWorkers,
}: ServiceDetailPageProps) => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const { user } = useSelector((state: { user: any }) => state.user);

  const [service, setService] = useState<any>(initialService);
  const [loadingService, setLoadingService] = useState<boolean>(!initialService);
  const [workers, setWorkers] = useState<any>(() =>
    initialWorkers != null ? normalizeWorkersListResponse(initialWorkers) : {}
  );
  const [isLoadingWorkers, setIsLoadingWorkers] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize] = useState<number>(12);
  const serviceId = getServiceId(service);
  const serviceContentName =
    service?.mlname?.[i18n.language] || service?.name || "";

  useEffect(() => {
    if (!serviceId || !serviceContentName) return;
    trackMetaViewContent({
      content_name: serviceContentName,
      content_ids: String(serviceId),
      content_type: "product",
      content_category: "service",
    });
  }, [serviceId, serviceContentName]);

  // Request quote selection mode:
  // - manual: client selects up to 3 providers via checkboxes
  // - auto: client sends to nearest providers (backend auto-discovers)
  const [quoteSelectionMode, setQuoteSelectionMode] = useState<'manual' | 'auto'>('auto');
  const [selectedProviders, setSelectedProviders] = useState<WorkerProfile[]>([]);
  const [requestQuotePopupShow, setRequestQuotePopupShow] = useState(false);
  const showQuoteSelectionToggle =
    isAuthenticated() && user?.type === "user";
  const effectiveQuoteSelectionMode = showQuoteSelectionToggle
    ? quoteSelectionMode
    : "auto";

  const toggleProviderSelection = (candidate: WorkerProfile) => {
    setSelectedProviders((prev) => {
      const providerUserId = String(candidate?.user?._id);
      const alreadySelected = prev.some((p) => String(p?.user?._id) === providerUserId);

      if (alreadySelected) {
        return prev.filter((p) => String(p?.user?._id) !== providerUserId);
      }

      if (prev.length >= 3) return prev;
      return [...prev, candidate];
    });
  };

  useEffect(() => {
    if (quoteSelectionMode === "auto") {
      setSelectedProviders([]);
    }
  }, [quoteSelectionMode]);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;

    const fetchService = async () => {
      if (!service) setLoadingService(true);
      try {
        const decodedSlug = slug.replace(/-/g, " ");
        let resolved: any = null;

        try {
          const res = await api.getServiceByName(decodedSlug);
          resolved = res?.data || res || null;
        } catch {
          resolved = null;
        }

        // Accented names (e.g. plomería) do not match byname("plomeria" / "plomera")
        if (!resolved) {
          const categories = await api.getServiceCategories();
          const list = normalizeServiceCategoriesResponse(categories);
          resolved = findServiceBySlugInCategories(list, slug);
        }

        if (!cancelled) {
          setService(resolved || null);
        }
      } catch (err) {
        console.error("Error fetching service:", err);
        if (!cancelled) setService(null);
      } finally {
        if (!cancelled) setLoadingService(false);
      }
    };

    fetchService();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    if (!serviceId) return;

    const fetchWorkers = async () => {
      setIsLoadingWorkers(true);
      try {
        const payload = { services: [serviceId], page, limit: pageSize };
        const result = await api.getFilterWorkersList(payload);
        setWorkers(normalizeWorkersListResponse(result));
      } catch (error) {
        console.error("Error fetching workers:", error);
        setWorkers(normalizeWorkersListResponse(null));
      } finally {
        setIsLoadingWorkers(false);
      }
    };
    fetchWorkers();
  }, [serviceId, page, pageSize]);

  const handleChangePage = (_e: unknown, newPage: number) => setPage(newPage);

  const filteredWorkers = useMemo(
    () =>
      (workers?.data || []).filter(
        (candidate: any) => candidate?.user?._id !== user?._id
      ),
    [workers, user]
  );

  const content = useMemo(
    () =>
      filteredWorkers?.map((candidate: any) => {
        const isSelected =
          selectedProviders.findIndex((p) => String(p?.user?._id) === String(candidate?.user?._id)) !== -1;
        const selectionDisabled =
          !isSelected && selectedProviders.length >= 3;

        return (
          <WorkerProfileCard
            key={candidate.id || candidate._id}
            candidate={candidate}
            selectionMode={effectiveQuoteSelectionMode}
            isSelected={isSelected}
            selectionDisabled={selectionDisabled}
            onToggleSelected={toggleProviderSelection}
          />
        );
      }),
    [filteredWorkers, selectedProviders, effectiveQuoteSelectionMode]
  );

  // ===================================
  // 🧩 UI Rendering
  // ===================================
  if (loadingService) {
    return (
      <div className="flex justify-center items-center h-screen text-gray-600">
        <span className="animate-spin h-6 w-6 border-2 border-gray-400 border-t-transparent rounded-full mr-2"></span>
        {t("loadingServiceDetails")}...
      </div>
    );
  }

  if (!service) {
    return (
      <div className="flex flex-col items-center justify-center h-screen text-gray-600">
        <p>{t("serviceNotFound")}</p>
      </div>
    );
  }

  const heroImage =
    service.imageURL || "/images/resource/service-placeholder.jpg";


    const pageTitle = service?.mlname?.[i18n.language] || service?.name || "Service Details";
const pageDescription = service?.mldescription?.[i18n.language] || service?.description || "";
const metaKeywords = service?.metadata?.join(", ");
console.log("metakeywords",metaKeywords)
//const heroImage = service?.imageURL || "/images/background/1.jpg";

  return (
<>
    {/* <Helmet>
  <title>{pageTitle}</title>
  {pageDescription && <meta name="description" content={pageDescription} />}
  {metaKeywords && <meta name="keywords" content={metaKeywords} />}
  {heroImage && <meta property="og:image" content={heroImage} />}
  <meta property="og:title" content={pageTitle} />
  <meta property="og:description" content={pageDescription} />
</Helmet> */}

    <div className="bg-[#f5f5f3] min-h-screen">
      {/* === HERO SECTION === */}
   {/* === HERO SECTION (Background Banner Only) === */}
<div className="relative w-full h-[160px] sm:h-[220px]  rounded-lg overflow-hidden  mt-28 px-6">
  {/* Background Image */}
  <img
    src={service.imageURL || "/images/background/1.jpg"}
    alt={service.name || "Service banner"}
    className="w-full h-full object-cover brightness-90"
    onError={(e) => {
      // fallback: hide image, show blue background only
      e.currentTarget.style.display = "none";
    }}
  />

  {/* Gradient Overlay */}
 

  {/* Title / Text */}
  <div className="absolute bottom-6 left-6 text-white max-w-3xl pl-10">
    <h1 className="text-2xl sm:text-3xl font-bold">
      {service.mlname?.[i18n.language] || service.name}
    </h1>

  </div>
</div>


      {/* === SERVICE DETAILS CARD === */}
      <div className="bg-white mx-6 p-8 rounded-xl shadow-md mb-10 mt-3">
        {/* Description */}
        {service.description && (
          <>
            <h2 className="text-2xl font-semibold text-gray-800 mb-3">
              {t("description")}
            </h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              {service.mldescription?.[i18n.language] ||
                service.description ||
                t("noDescriptionAvailable")}
            </p>
          </>
        )}

        {/* Metadata Tags */}
        {/* {service.metadata?.length > 0 && (
          <>
            <h2 className="text-2xl font-semibold text-gray-800 mb-3">
              {t("metadataTags")}
            </h2>
            <div className="flex flex-wrap gap-3 mb-6">
              {service.metadata.map((tag: string, idx: number) => (
                <span
                  key={idx}
                  className="bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-sm font-medium shadow-sm"
                >
                  {tag}
                </span>
              ))}
            </div>
          </>
        )} */}

        {/* Skills */}
        <h2 className="text-2xl font-semibold text-gray-800 mb-3">
          {t("skillsRequired")}
        </h2>
        <ul className="flex flex-wrap gap-3">
          {service.skills?.length ? (
            service.skills.map((skill: any) => (
              <li
                key={skill.id || skill._id}
                className="px-4 py-2 bg-green-50 text-green-700 rounded-md text-sm shadow-sm"
              >
                {skill.name}
              </li>
            ))
          ) : (
            <p className="text-gray-500">{t("noSkillsAvailable")}</p>
          )}
        </ul>
      </div>

      {/* === PROVIDERS === */}
      <div className="bg-[#f5f5f3] mx-6 p-8 rounded-xl shadow-md">
        <h2 className="text-2xl font-semibold text-gray-800 mb-6">
          {t("availableProviders")}
        </h2>

        {showQuoteSelectionToggle && (
          <QuoteSelectionModeToggle
            mode={quoteSelectionMode}
            onChange={setQuoteSelectionMode}
            className="mb-4"
          />
        )}

        <Spinner isLoading={isLoadingWorkers} size="medium" classname="mb-6" />

        {!isLoadingWorkers && filteredWorkers?.length > 0 && (
          <>
            <div className="grid md:grid-cols-4 sm:grid-cols-2 grid-cols-1 gap-6">
              {content}
            </div>

            {effectiveQuoteSelectionMode === "manual" && selectedProviders.length > 0 && (
              <div className="fixed bottom-0 left-0 right-0 z-[1000] bg-white border-t border-gray-200 px-6 py-4">
                <div className="max-w-5xl mx-auto flex items-center justify-between gap-4">
                  <div className="text-sm text-gray-700">
                    Selected:{" "}
                    <span className="font-semibold text-darkBlue">
                      {selectedProviders.length}
                    </span>{" "}
                    provider{selectedProviders.length === 1 ? "" : "s"}
                  </div>
                  <button
                    type="button"
                    className="px-6 py-2 rounded-3xl bg-darkBlue text-white font-semibold"
                    onClick={() => setRequestQuotePopupShow(true)}
                  >
                    Send Request Quote
                  </button>
                </div>
              </div>
            )}

            <div className="flex justify-center mt-6">
              <Pagination
                count={
                  workers?.totalPages ||
                  Math.ceil(
                    ((workers?.total ?? workers?.totalItems ?? 0) as number) /
                      pageSize
                  ) ||
                  1
                }
                color="primary"
                variant="outlined"
                page={page}
                onChange={handleChangePage}
                showFirstButton
                showLastButton
                siblingCount={0}
              />
            </div>
          </>
        )}

        {!isLoadingWorkers && filteredWorkers?.length === 0 && (
          <p className="text-base font-semibold text-center text-gray-600">
            {t("noDataAvailableAgainstFilters")}
          </p>
        )}
      </div>
    </div>

      {/* Manual selection popup: send request quote to exactly the selected providers */}
      <Popup
        isOpen={requestQuotePopupShow}
        handleClose={() => setRequestQuotePopupShow(false)}
        handleSubmit={() => {}}
        size={"sm"}
        className="bg-darkBlue"
        headerTitle=""
        contentSx={{ padding: 0 }}
        isShowCloseIcon={false}
        isShowDeleteIcon={false}
        slotProps={{ preventOutsideClose: true }}
        bodyChildren={
          requestQuotePopupShow ? (
            <Suspense fallback={<InlineLazyFallback />}>
              <RequestQuote
                workerid={getProviderUserId(selectedProviders[0]) || undefined}
                workerProfileId={getWorkerProfileId(selectedProviders[0]) || undefined}
                providerIds={selectedProviders
                  .map((p) => getProviderUserId(p))
                  .filter((id): id is string => Boolean(id))}
                onSuccess={() => {
                  setRequestQuotePopupShow(false);
                  setSelectedProviders([]);
                }}
              />
            </Suspense>
          ) : null
        }
      />
    </>
  );
};

export default ServiceDetailPage;
